import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, Link, Typography } from '@mui/material';
import Drawer from '@mui/material/Drawer/Drawer';
import * as Sentry from '@sentry/react';
import {
  capitalizeWords,
  getCertificatePreferencesAsString,
  getCourtLocation,
  JobResponse,
  JobStatusType,
  PaymentStatusResponse,
  transformDate,
} from '../../pages/dashboard/util';
import DetailedJobViewSkeleton from './components/detailed-job-components/DetailedJobViewSkeleton';
import CloseIcon from '@mui/icons-material/Close';
import JobDocuments from './components/detailed-job-components/JobDocuments';
import { JobDocumentsHandle } from './components/detailed-job-components/JobDocuments';
import { apiInstance } from '../../api/apiInstance';
import DownloadSection from './components/detailed-job-components/DownloadSection';
import OverviewSection from './components/detailed-job-components/OverviewSection';
import PreferencesSection from './components/detailed-job-components/PreferencesSection';
import EditPreferencesSection, { EditPreferencesSectionHandle } from './components/detailed-job-components/EditPreferencesSection';
import Invoice from './components/detailed-job-components/Invoice';
import ChainOfCustody from './components/detailed-job-components/ChainOfCustody';
import BkSnackBar from '../snackbar/BkSnackBar';
import { downloadFile, normalizeInvoice, summarizeLegacyMailingTypes, summarizeMailingTypes } from 'src/utils/utility-functions';
import { useSelector } from 'react-redux';
import { RootState } from 'src/store/store';
import { OrganizationType } from 'src/utils/enums/organization-type.enum';
import JobTrackingNumbers from './components/detailed-job-components/JobTrackingNumbers';
import PrintDocumentSection from './components/detailed-job-components/PrintDocumentSection';
import ServiceBadge from 'src/components/service-badge/ServiceBadge';
import PrintGenerationProgress from './components/detailed-job-components/PrintGenerationProgress';
import ContactInformation from './components/detailed-job-components/ContactInformation';
import UnsavedChangesDialog from './UnsavedChangesDialog';

interface DetailedJobViewProps {
  open: boolean;
  onClose: () => void;
  job: JobResponse | null;
  onJobCancel?: (id: number) => void;
  onStatusChange?: (jobInfo: { id: number; status: JobStatusType }) => void;
}

interface SnackbarProps {
  type: 'success' | 'error';
  msg: string;
}

const mapPaymentStatus = {
  paid: 'Successful',
  unpaid: 'Pending',
  failed: 'Rejected',
  refunded: 'Refunded',
};

// ---------------------------------------------------------------------------
// DetailedJobView — main component
// ---------------------------------------------------------------------------

const DetailedJobView = ({
  open,
  onClose,
  job,
  onJobCancel = () => { },
  onStatusChange = () => { },
}: DetailedJobViewProps) => {
  const [showSnackbarMsg, setShowSnackbarMsg] = useState<SnackbarProps>({
    msg: '',
    type: 'success',
  });
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const [localInvoice, setLocalInvoice] = useState(job?.invoice ?? null);
  const [localEditAuditLogs, setLocalEditAuditLogs] = useState(job?.editAuditLogs ?? null);

  // Sync local invoice when job prop changes
  useEffect(() => {
    setLocalInvoice(job?.invoice ?? null);
  }, [job?.invoice]);

  // Sync local audit logs when job prop changes
  useEffect(() => {
    setLocalEditAuditLogs(job?.editAuditLogs ?? null);
  }, [job?.editAuditLogs]);

  const jobDocumentsRef = useRef<JobDocumentsHandle>(null);
  const editPreferencesRef = useRef<EditPreferencesSectionHandle>(null);

  // Tracks unsaved document edits reported by JobDocuments. Used to gate the
  // "Move to In Queue" status transition (admins may only move a job to In
  // Queue once all document changes in the side panel are saved).
  const [hasUnsavedDocumentChanges, setHasUnsavedDocumentChanges] = useState(false);
  const [hasUnsavedPreferencesChanges, setHasUnsavedPreferencesChanges] = useState(false);

  const currentUser = useSelector((state: RootState) => state.user.currentUser);
  const isCosAdmin = currentUser.teamRole === 'admin';
  const isCosMember = currentUser.teamRole === 'admin' || currentUser.teamRole === 'support';

  const isChapter13Job =
    job?.organization?.type === OrganizationType.CHAPTER_13_TRUSTEE ||
    currentUser?.organization?.type === OrganizationType.CHAPTER_13_TRUSTEE;

  const handleCloseAttempt = useCallback(() => {
    if (jobDocumentsRef.current?.hasUnsavedChanges() || editPreferencesRef.current?.hasUnsavedChanges()) {
      setShowUnsavedDialog(true);
    } else {
      onClose();
    }
  }, [onClose]);

  const handleSaveComplete = useCallback(async () => {
    if (!job) return;
    try {
      const response = await apiInstance.get(`/job/item/${job.id}`);
      const updatedJob = response.data.data;
      if (updatedJob?.editAuditLogs) {
        setLocalEditAuditLogs(updatedJob.editAuditLogs);
      }
    } catch (err) {
      console.error('Failed to refresh audit logs:', err);
    }
  }, [job]);

  const handleSaveAndClose = useCallback(async () => {
    setShowUnsavedDialog(false);
    // Only JobDocuments supports a "save-and-close" path via the ref;
    // preferences edits require the user to explicitly click "Save Changes"
    // in the section. If only preferences have unsaved changes, discard them.
    if (jobDocumentsRef.current?.hasUnsavedChanges()) {
      await jobDocumentsRef.current?.saveChanges();
    }
    editPreferencesRef.current?.discardChanges();
  }, []);

  const handleDiscardAndClose = useCallback(() => {
    setShowUnsavedDialog(false);
    jobDocumentsRef.current?.discardChanges();
    editPreferencesRef.current?.discardChanges();
  }, []);

  // Warn on page reload/navigation when there are unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (
        jobDocumentsRef.current?.hasUnsavedChanges() ||
        jobDocumentsRef.current?.hasActiveInput() ||
        editPreferencesRef.current?.hasUnsavedChanges()
      ) {
        e.preventDefault();
      }
    };

    if (open) {
      window.addEventListener('beforeunload', handleBeforeUnload);
    }

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [open]);

  const getPaymentStatus = (status: PaymentStatusResponse) => {
    if (isChapter13Job) {
      return null;
    }

    if (status === undefined) {
      return 'Pending';
    }

    return mapPaymentStatus[status];
  };

  const downloadAddresses = async (trackingNumber: string, id: number) => {
    try {
      await downloadFile(`/job/item/${id}/addresses/download`, `${trackingNumber}_addresses`);
    } catch (err: any) {
      setShowSnackbarMsg({ msg: err.message, type: 'error' });
    }
  };

  const downloadCertificate = async (trackingNumber: string, id: number) => {
    try {
      await downloadFile(`/job/item/${id}/certificate/download`, `${trackingNumber}_certificate`);
    } catch (err: any) {
      setShowSnackbarMsg({ msg: err.message, type: 'error' });
    }
  };

  const downloadJobDocument = async (filename: string, id: number) => {
    try {
      await downloadFile(`/job/documents/${id}/download`, filename);
    } catch (err: any) {
      setShowSnackbarMsg({ msg: err.message, type: 'error' });
    }
  };

  const requestCertificate = async (id: number) => {
    try {
      await apiInstance.post(`/job/item/${id}/certificate/request`);
    } catch (err: any) {
      setShowSnackbarMsg({ msg: err.response.data.message, type: 'error' });
    }
  };

  const formatDebtorName = (job: JobResponse) => {
    if (job.debtorName && job.jointDebtorName) {
      return `${job.debtorName} and ${job.jointDebtorName}`;
    }
    return job.debtorName || null;
  };

  if (!job) {
    return (
      <Drawer anchor="right" open={true}>
        <DetailedJobViewSkeleton />
      </Drawer>
    );
  }

  const overviewSectionItems = [
    {
      label: 'Job',
      value: job.trackingNumber || null,
    },
    {
      label: 'Payment Status',
      value: getPaymentStatus(job?.order?.paymentStatus),
    },
    {
      label: 'Source',
      value: job.integrationPartner ? job.integrationPartner.toUpperCase() : 'COS',
    },
    {
      label: 'User',
      value: job.user ? `${job.user.firstName} ${job.user.lastName}` : null,
    },
    {
      label: 'Date & Time',
      value: transformDate(job.createdAt),
    },
    {
      label: 'Type',
      value: job.type ? capitalizeWords(job.type) : null,
    },
    {
      label: 'Court',
      value: getCourtLocation(job.courtJurisdiction, job.courtLocation),
    },
    {
      label: job?.caseNumbers?.length > 1 ? 'Case Numbers' : 'Case Number',
      value: job?.caseNumbers?.join(', ') || null,
    },
    // Estate Party Name is the primary identifier for Probate Estate jobs,
    // mirroring Debtor Name's placement on Bankruptcy jobs. Only Probate Estate
    // jobs carry `adversaryProceeding.estatePartyName`, so this line is absent
    // (null value → not rendered) on Federal Court, State Court, Bankruptcy, and
    // Direct Mailing jobs, leaving those side screens unchanged.
    {
      label: 'Estate Party Name',
      value: job.adversaryProceeding?.estatePartyName || null,
    },
    {
      label: 'Debtor Name',
      value: formatDebtorName(job),
    },
    {
      label: 'Adversary Number',
      value: job.adversaryProceeding?.adversaryNumber || null,
    },
    {
      label: 'Adversary Plaintiff',
      value: job.adversaryProceeding?.adversaryPlaintiff || null,
    },
    {
      label: 'Adversary Defendant',
      value: job.adversaryProceeding?.adversaryDefendant || null,
    },
    {
      label: 'Documents',
      value: job.documents ? job.documents.length : null,
    },
    {
      label: 'Pages',
      value: job.pagesCount || null,
    },
    {
      label: 'Sheets',
      value: job.sheetsCount || null,
    },
    {
      label: 'Mailing Types',
      value: job.mailingTypes
        ? summarizeLegacyMailingTypes(job.mailingTypes)
        : summarizeMailingTypes(job.mailingTypeCounts) || 'N/A',
    },
    {
      label: 'Parties',
      value: job.partiesCount || null,
    },
  ];

  const retAddr = job.returnAddress;
  const preferencesSectionItems = [
    {
      label: 'Sides',
      value: job.orderPreferences ? (job.orderPreferences.sides === 'double_sided' ? 'Duplex' : 'Simplex') : null,
    },
    {
      label: 'Format',
      value: job.orderPreferences?.format ?? null,
    },
    {
      label: 'Service',
      value: job.orderPreferences?.service ? <ServiceBadge service={job.orderPreferences.service} /> : null,
    },
    {
      label: 'Return Address',
      value: job.returnAddress
        ? [
          retAddr.addressName,
          retAddr.addressOne,
          retAddr.addressTwo,
          `${retAddr.city}, ${retAddr.state} ${retAddr.zipCode}`,
        ]
          .filter(Boolean)
          .join(', ')
        : null,
    },
    {
      label: 'Certificate of Service',
      // Guard on certificatePreferences (not just signature) — that's what's
      // actually read here, and partner-API jobs have a signature but no
      // preferences.
      value:
        job.signature && job.certificatePreferences
          ? getCertificatePreferencesAsString(job.certificatePreferences)
          : null,
    },
    {
      label: 'Signature Block',
      value: job.signature
        ? [
          job.signature.fullName,
          job.signature.addressOne,
          job.signature.addressTwo,
          `${job.signature.city}, ${job.signature.state} ${job.signature.zipCode}`,
        ]
          .filter(Boolean)
          .join(', ')
        : null,
    },
    ...(job.dmMailingType || isChapter13Job
      ? []
      : [
        {
          label: 'Return Envelope',
          value: job.returnEnvelope ? 'Yes' : 'No',
        },
        {
          label: 'Return Envelope Address',
          value: job.returnEnvelope
            ? [
              job.returnEnvelope.name,
              job.returnEnvelope.office,
              job.returnEnvelope.address,
              `${job.returnEnvelope.city}, ${job.returnEnvelope.state} ${job.returnEnvelope.zipCode}`,
            ].join(', ')
            : 'N/A',
        },
        {
          label: 'Return Envelope Postage',
          value: job.returnEnvelope ? (job.returnEnvelope.isPostageIncluded ? 'Included' : 'Excluded') : 'N/A',
        },
      ]),
  ];

  const showCancelButton =
    isCosAdmin &&
    job.status !== 'cancelled' &&
    job.status !== 'completed' &&
    job.status !== 'completed_payment_declined';

  return (
    <Drawer anchor="right" open={open} onClose={handleCloseAttempt} PaperProps={{ sx: { width: '100%', maxWidth: '50rem' } }}>
      <UnsavedChangesDialog
        open={showUnsavedDialog}
        onClose={() => setShowUnsavedDialog(false)}
        onSave={handleSaveAndClose}
        onDiscard={handleDiscardAndClose}
      />
      <BkSnackBar
        open={!!showSnackbarMsg.msg}
        handleCloseSnackbar={() => setShowSnackbarMsg({ type: 'success', msg: '' })}
        snackMessage={showSnackbarMsg.msg}
        severity={showSnackbarMsg.type}
      />
      {job?.paymentLink && (
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            padding: '1rem',
            backgroundColor: '#FFE8E7',
            justifyContent: 'center',
          }}
        >
          <Typography
            sx={{ fontSize: '0.875rem', lineHeight: '1.25rem', fontWeight: 600, color: '#AC3737', textAlign: 'center' }}
          >
            To resolve your outstanding payment, please use this{' '}
            <Link href={job?.paymentLink} target="_blank" rel="noopener">
              payment link
            </Link>
            .
          </Typography>
        </Box>
      )}
      {/* Error boundary around the job detail body. Partner-API jobs and drafts
          can carry null fields the panel wasn't built for (e.g. missing
          certificatePreferences / orderPreferences / invoice), and a single
          unguarded access used to throw during render and white-screen the whole
          app. This degrades a render failure to an in-panel message + Sentry
          report, keeping the rest of the app usable. Field-level guards still
          handle the known cases so this fallback is a last resort, not the norm. */}
      <Sentry.ErrorBoundary
        fallback={() => (
          <div style={{ padding: '1.25rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">Job {job.trackingNumber}</Typography>
              <button style={{ all: 'unset', cursor: 'pointer' }} onClick={handleCloseAttempt}>
                <CloseIcon />
              </button>
            </div>
            <Box sx={{ mt: '2rem', textAlign: 'center' }}>
              <Typography sx={{ fontWeight: 600, mb: '0.5rem' }}>Couldn&apos;t load job details</Typography>
              <Typography variant="body2" sx={{ color: '#666', mb: '1.5rem' }}>
                Something went wrong displaying this job. The rest of the app is unaffected.
              </Typography>
              <Button variant="outlined" onClick={handleCloseAttempt}>Close</Button>
            </Box>
          </div>
        )}
      >
      <div style={{ padding: '1.25rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="h6">Job {job.trackingNumber}</Typography>
          <button style={{ all: 'unset', cursor: 'pointer' }} onClick={handleCloseAttempt}>
            <CloseIcon />
          </button>
        </div>
        <OverviewSection
          onJobCancel={() => onJobCancel(job.id)}
          jobId={job.id}
          isCosAdmin={isCosAdmin}
          isCosMember={isCosMember}
          onHoldReason={isCosMember ? (job.onHoldReason ?? null) : null}
          onStatusChange={onStatusChange}
          statusHistory={job.statusHistory}
          showCancelButton={showCancelButton}
          items={overviewSectionItems}
          jobStatus={job.status}
          hasUnsavedDocumentChanges={hasUnsavedDocumentChanges}
        />
        {isCosMember && <PrintGenerationProgress jobId={job.id} />}
        {isCosMember && (
          <ContactInformation
            submitterFirstName={job.submitterFirstName}
            submitterLastName={job.submitterLastName}
            submitterEmail={job.submitterEmail}
            organizationPhone={job.organizationPhone}
            organizationCountryCode={job.organizationCountryCode}
          />
        )}
        {job.orderPreferences && isCosAdmin && job.status === 'on_hold' && job.onHoldReason === 'Manual' ? (
          <EditPreferencesSection
            ref={editPreferencesRef}
            items={preferencesSectionItems}
            currentService={(job.orderPreferences?.service as 'normal' | 'rush') || 'normal'}
            jobId={job.id}
            isCosAdmin={isCosAdmin}
            jobStatus={job.status}
            onInvoiceRecalculated={setLocalInvoice}
            onUnsavedChangesChange={setHasUnsavedPreferencesChanges}
            onSaveComplete={handleSaveComplete}
          />
        ) : (
          job.orderPreferences && <PreferencesSection items={preferencesSectionItems} />
        )}
        {job.printDocuments && isCosMember && <PrintDocumentSection printDocuments={job.printDocuments} />}
        <JobDocuments
          ref={jobDocumentsRef}
          onDownloadDocument={downloadJobDocument}
          documents={job.documents}
          isCosAdmin={isCosAdmin}
          jobStatus={job.status}
          jobId={job.id}
          onDocumentsChange={() => { }}
          onUnsavedChangesChange={setHasUnsavedDocumentChanges}
          onInvoiceRecalculated={setLocalInvoice}
          onSaveComplete={handleSaveComplete}
          currentInvoiceTotal={localInvoice?.total ?? 0}
        />
        <DownloadSection
          jobCreatedAt={job.createdAt}
          isCertificateRequested={job.isCertificateRequested}
          isCertificateRequestable={job.isCertificateRequestable}
          isCertificateDownloadable={job.isCertificateDownloadable}
          onDownloadCertificate={() => downloadCertificate(job.trackingNumber, job.id)}
          onAddressesDownload={() => downloadAddresses(job.trackingNumber, job.id)}
          onRequestCertificate={() => requestCertificate(job.id)}
        />
        <ChainOfCustody
          ip={job.httpClient?.ip}
          userAgent={job.httpClient?.userAgent}
          host={job.httpClient?.host}
          userId={job.user?.id}
          createdAt={job.createdAt}
          statusHistory={job.statusHistory}
          editAuditLogs={localEditAuditLogs || null}
          isCosMember={isCosMember}
        />
        <JobTrackingNumbers
          isCosMember={isCosMember}
          jobId={job.id}
          updateSnackbarMsg={setShowSnackbarMsg}
        />
        {localInvoice !== null && (
          <Invoice invoiceData={normalizeInvoice(localInvoice)} trackingNumber={job.trackingNumber} />
        )}
      </div>
      </Sentry.ErrorBoundary>
    </Drawer>
  );
};

export default DetailedJobView;
